/**
 * AudioMidiClock - AudioContext-based MIDI Clock
 *
 * Replaces setInterval-based MidiClock with AudioScheduler timing
 * for drift-free MIDI clock at any BPM including high tempos.
 *
 * Uses the "Tale of Two Clocks" pattern via AudioScheduler:
 * - AudioContext.currentTime for precise scheduling
 * - Worker-timers for background-tab-resistant tick callbacks
 *
 * MIDI Clock Messages:
 * - 0xF8: Timing Clock (sent 24 times per quarter note)
 * - 0xFA: Start
 * - 0xFB: Continue
 * - 0xFC: Stop
 */

import { Output } from "webmidi";
import { AudioScheduler, type ScheduledEvent } from "~/lib/audio/AudioScheduler";

export interface AudioMidiClockOptions {
  /** User-facing latency compensation in ms (0 = lowest, adds to lookAhead) */
  latencyMs?: number;
}

export class AudioMidiClock {
  private scheduler: AudioScheduler;
  private bpm: number;
  private isRunning = false;
  private outputs: Output[] = [];
  private clocksSent = 0;
  private clockEvent: ScheduledEvent | null = null;
  private latencyMs: number;

  // Drift tracking
  private lastClockAudioTime = 0;
  private driftSamples: number[] = [];
  private maxDriftSamples = 48; // 2 quarter notes worth of samples

  // MIDI Clock constants
  private static readonly PPQN = 24;
  private static readonly CLOCK_MESSAGE = 0xf8;
  private static readonly START_MESSAGE = 0xfa;
  private static readonly CONTINUE_MESSAGE = 0xfb;
  private static readonly STOP_MESSAGE = 0xfc;

  constructor(scheduler: AudioScheduler, bpm: number, options: AudioMidiClockOptions = {}) {
    this.scheduler = scheduler;
    this.bpm = bpm;
    this.latencyMs = options.latencyMs ?? 0;
  }

  /**
   * Calculate interval between MIDI clock pulses in seconds
   */
  private calculateClockIntervalSeconds(): number {
    return 60 / this.bpm / AudioMidiClock.PPQN;
  }

  /**
   * Set the tempo (BPM) - updates live without restarting
   */
  setBPM(bpm: number): void {
    this.bpm = bpm;
    this.scheduler.setBPM(bpm);
    // Clock interval updates automatically on next scheduled pulse
    console.log(`[AUDIO MIDI CLOCK] BPM set to ${bpm}`);
  }

  /**
   * Get current BPM
   */
  getBPM(): number {
    return this.bpm;
  }

  /**
   * Set latency compensation
   */
  setLatency(latencyMs: number): void {
    this.latencyMs = latencyMs;
  }

  /**
   * Get latency compensation
   */
  getLatency(): number {
    return this.latencyMs;
  }

  /**
   * Add MIDI output to receive clock messages
   */
  addOutput(output: Output): void {
    if (!this.outputs.find(o => o.id === output.id)) {
      this.outputs.push(output);
    }
  }

  /**
   * Remove MIDI output
   */
  removeOutput(output: Output): void {
    this.outputs = this.outputs.filter(o => o.id !== output.id);
  }

  /**
   * Clear all outputs
   */
  clearOutputs(): void {
    this.outputs = [];
  }

  /**
   * Get connected outputs
   */
  getOutputs(): Output[] {
    return [...this.outputs];
  }

  /**
   * Send a system real-time message to all outputs
   * Optionally with a timestamp for sample-accurate timing
   */
  private sendMessage(message: number, timestamp?: number): void {
    this.outputs.forEach(output => {
      try {
        if (timestamp !== undefined) {
          output.send([message], { time: timestamp });
        } else {
          output.send([message]);
        }
      } catch (error) {
        console.error(
          `[AUDIO MIDI CLOCK] Failed to send ${message.toString(16)} to ${output.name}:`,
          error
        );
      }
    });
  }

  /**
   * Send MIDI Clock pulse with drift tracking
   */
  private sendClock(audioTime: number): void {
    // Convert audio time to MIDI timestamp for sample-accurate delivery
    const midiTimestamp = this.scheduler.audioTimeToMidiTimestamp(audioTime);
    this.sendMessage(AudioMidiClock.CLOCK_MESSAGE, midiTimestamp);
    this.clocksSent++;

    // Track drift
    if (this.lastClockAudioTime > 0) {
      const expectedInterval = this.calculateClockIntervalSeconds();
      const actualInterval = audioTime - this.lastClockAudioTime;
      const drift = (actualInterval - expectedInterval) * 1000; // Convert to ms
      this.driftSamples.push(drift);
      if (this.driftSamples.length > this.maxDriftSamples) {
        this.driftSamples.shift();
      }
    }
    this.lastClockAudioTime = audioTime;

    // Log every quarter note
    if (this.clocksSent % 24 === 0) {
      const avgDrift = this.getAverageDrift();
      console.log(
        `[AUDIO MIDI CLOCK] Quarter note ${this.clocksSent / 24}, ` +
        `avg drift: ${avgDrift.toFixed(3)}ms`
      );
    }
  }

  /**
   * Get average drift in ms (for monitoring)
   */
  getAverageDrift(): number {
    if (this.driftSamples.length === 0) return 0;
    return this.driftSamples.reduce((a, b) => a + b, 0) / this.driftSamples.length;
  }

  /**
   * Get max absolute drift in ms
   */
  getMaxDrift(): number {
    if (this.driftSamples.length === 0) return 0;
    return Math.max(...this.driftSamples.map(Math.abs));
  }

  /**
   * Start sending MIDI Clock
   */
  start(): void {
    if (this.isRunning) return;
    if (this.outputs.length === 0) {
      console.warn("[AUDIO MIDI CLOCK] No outputs configured");
      return;
    }

    this.isRunning = true;
    this.clocksSent = 0;
    this.driftSamples = [];
    this.lastClockAudioTime = 0;

    // Send START message immediately
    this.sendMessage(AudioMidiClock.START_MESSAGE);

    // Schedule repeating clock pulses using AudioScheduler
    const interval = this.calculateClockIntervalSeconds();
    const startTime = this.scheduler.currentTime;

    this.clockEvent = this.scheduler.scheduleRepeating(
      (event) => {
        this.sendClock(event.deadline);
      },
      startTime,
      interval
    );

    console.log(
      `[AUDIO MIDI CLOCK] Started at ${this.bpm} BPM ` +
      `(${(interval * 1000).toFixed(2)}ms interval)`
    );
  }

  /**
   * Continue sending MIDI Clock from current position
   */
  continue(): void {
    if (this.isRunning) return;
    if (this.outputs.length === 0) return;

    this.isRunning = true;
    this.lastClockAudioTime = 0;

    this.sendMessage(AudioMidiClock.CONTINUE_MESSAGE);

    const interval = this.calculateClockIntervalSeconds();
    const startTime = this.scheduler.currentTime;

    this.clockEvent = this.scheduler.scheduleRepeating(
      (event) => {
        this.sendClock(event.deadline);
      },
      startTime,
      interval
    );

    console.log(`[AUDIO MIDI CLOCK] Continued at ${this.bpm} BPM`);
  }

  /**
   * Stop sending MIDI Clock
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;

    // Cancel scheduled clock events
    if (this.clockEvent) {
      this.scheduler.cancel(this.clockEvent);
      this.clockEvent = null;
    }

    this.sendMessage(AudioMidiClock.STOP_MESSAGE);
    console.log(`[AUDIO MIDI CLOCK] Stopped after ${this.clocksSent} clocks`);
  }

  /**
   * Check if clock is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get number of clocks sent
   */
  getClocksSent(): number {
    return this.clocksSent;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop();
    this.clearOutputs();
    this.driftSamples = [];
  }
}
